import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, ButtonBase, TextField, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useQuery } from 'react-query';
import { DateTime } from 'luxon';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import { appointmentApi } from '../../utils/api';
import { appointmentService } from '../../services/appointmentService';
import { withOfflineFallback, readAppointmentsInRange, readTimeBlocks, useIsOffline } from '../../offline';
import { useRBAC } from '../../auth/useRBAC';
import { calendarSettingsControls } from '../../auth/pageControls';
import PricingItemDialog from '../../components/ServiceCatalog/PricingItemDialog';
import type { ServiceCatalogItem } from '../../services/serviceCatalogService';
import { providerInitials } from './calendarGrid';
import { STATUSES, AppointmentStatus, isCancelled } from './appointmentStatus';

/*
 * Pieces shared by the appointment editor and the New appointment window (Schedule canvas boards
 * Edit and Book): section labels, when, provider with availability, the type dropdown, status.
 */

export const SectionLabel: FC<{ children: ReactNode; aside?: ReactNode; id?: string }> = ({ children, aside, id }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
    <Typography
      id={id}
      component="h3"
      sx={{ flex: 1, m: 0, fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}
    >
      {children}
    </Typography>
    {aside && <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{aside}</Typography>}
  </Box>
);

const pillSx = (on: boolean, accent = '#3B2E1A') => ({
  height: 34,
  px: 1.5,
  gap: 0.75,
  borderRadius: 999,
  fontSize: '0.82rem',
  fontWeight: on ? 600 : 500,
  border: '1px solid',
  borderColor: on ? accent : '#D1D5DB',
  bgcolor: on ? accent : 'background.paper',
  color: on ? '#fff' : '#3B2E1A',
});

/** A segmented control (Patient / Owner only / Barn, Clinic / Farm call, Tentative / Confirmed). */
export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T;
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', p: '3px', gap: '2px', bgcolor: '#F3F4F6', borderRadius: 2.5 }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            aria-pressed={on}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            sx={{
              flex: 1,
              py: 0.9,
              borderRadius: 2,
              fontSize: '0.82rem',
              fontWeight: on ? 600 : 500,
              color: on ? '#3B2E1A' : '#4B5563',
              bgcolor: on ? 'background.paper' : 'transparent',
              boxShadow: on ? '0 1px 2px rgba(17,17,17,0.08)' : 'none',
              '&.Mui-disabled': { color: '#9CA3AF' },
            }}
          >
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

const lengthLabel = (m: number) => (m < 60 ? `${m} min` : m === 60 ? '1 hr' : `${m / 60} hrs`);

/** Date, start and end, plus length chips that set the end (canvas "When"). */
/**
 * A date or time field hands back an Invalid Date while a part of it is empty (the minutes just
 * backspaced). Keep the last whole value until the field is whole again; passing it on crashed the page.
 */
const whole = (value: Date | null): value is Date => !!value && !Number.isNaN(value.getTime());

export const WhenFields: FC<{
  start: Date | null;
  minutes: number;
  onChange: (start: Date | null, minutes: number) => void;
  lengths?: number[];
}> = ({ start, minutes, onChange, lengths = [15, 30, 45, 60, 90, 120] }) => {
  // One End value per start and length: a new Date every render would make the End field reset a half-typed time.
  const end = useMemo(() => (start ? new Date(start.getTime() + minutes * 60_000) : null), [start, minutes]);
  const setDay = (day: Date | null) => {
    if (!whole(day)) return;
    const next = new Date(day);
    const from = start ?? new Date(new Date().setHours(9, 0, 0, 0));
    next.setHours(from.getHours(), from.getMinutes(), 0, 0);
    onChange(next, minutes);
  };
  const setStart = (time: Date | null) => {
    if (!whole(time)) return;
    const next = new Date(start ?? time);
    next.setHours(time.getHours(), time.getMinutes(), 0, 0);
    onChange(next, minutes);
  };
  const setEnd = (time: Date | null) => {
    if (!whole(time) || !start) return;
    const next = new Date(start);
    next.setHours(time.getHours(), time.getMinutes(), 0, 0);
    const m = Math.round((next.getTime() - start.getTime()) / 60_000);
    if (m >= 5) onChange(start, m);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.5fr 1fr 1fr' }, gap: 1.25 }}>
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
          <DatePicker label="Date" value={start} onChange={setDay} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
        </Box>
        <TimePicker label="Start" value={start} onChange={setStart} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
        <TimePicker label="End" value={end} onChange={setEnd} disabled={!start} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
      </Box>
      <Box role="group" aria-label="Length" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {lengths.map((m) => (
          <ButtonBase key={m} aria-pressed={m === minutes} onClick={() => onChange(start, m)} sx={pillSx(m === minutes)}>
            {lengthLabel(m)}
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

export interface ProviderOption {
  id: string;
  name: string;
}

/**
 * Who is busy at [start, start + minutes): the vet's appointments that day (not cancelled, not the
 * ones being edited) and their time blocks, plus clinic closures. Read through the same offline
 * fallbacks as the Schedule, so it answers from the replica offline.
 */
export const useProviderAvailability = (start: Date | null, minutes: number, ignoreIds: string[] = []) => {
  const ymd = start ? DateTime.fromJSDate(start).toFormat('yyyy-MM-dd') : '';
  const { data: dayAppointments = [] } = useQuery(
    ['appointments', 'availability', ymd],
    () => {
      // A save refetches every ['appointments', ...] query, disabled ones too, so this may run without a day.
      if (!start) return Promise.resolve([]);
      const dateFrom = startOfDay(start).toISOString();
      const dateTo = endOfDay(start).toISOString();
      return withOfflineFallback<any[]>(
        () => appointmentApi.list({ dateFrom, dateTo }),
        () => readAppointmentsInRange({ dateFrom, dateTo }).then((res) => res.data),
      )();
    },
    { enabled: !!start, staleTime: 30_000 },
  );
  const { data: timeBlocks = [] } = useQuery<any[]>(
    ['time-blocks'],
    withOfflineFallback(appointmentService.listTimeBlocks, () => readTimeBlocks()),
    { staleTime: 60_000 },
  );
  const ignore = ignoreIds.join('|');

  return useMemo(() => {
    const busy: Record<string, string> = {};
    if (!start) return busy;
    const from = start.getTime();
    const to = from + minutes * 60_000;
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const clock = (ms: number) => DateTime.fromMillis(ms, { zone }).toFormat('h:mm a');
    const skip = new Set(ignore ? ignore.split('|') : []);
    for (const apt of dayAppointments as any[]) {
      if (!apt.assignedVetId || skip.has(apt.id) || isCancelled(apt.status) || busy[apt.assignedVetId]) continue;
      const s = new Date(apt.date).getTime();
      const e = s + (apt.duration || 60) * 60_000;
      if (s < to && e > from) {
        busy[apt.assignedVetId] = `Busy · ${apt.horse?.name ?? apt.client?.name ?? apt.barn?.name ?? apt.type} ${clock(s)} - ${clock(e)}`;
      }
    }
    for (const block of timeBlocks as any[]) {
      const s = new Date(block.startTime).getTime();
      const e = new Date(block.endTime).getTime();
      if (!(s < to && e > from)) continue;
      if (block.vetId == null) {
        busy['*'] = `Clinic closed ${clock(s)} - ${clock(e)}`;
      } else if (!busy[block.vetId]) {
        busy[block.vetId] = `Blocked ${clock(s)} - ${clock(e)}${block.reason ? ` · ${block.reason}` : ''}`;
      }
    }
    return busy;
  }, [dayAppointments, timeBlocks, start, minutes, ignore]);
};

/** Providers as radio rows with "free at this time?" (canvas "Provider"), plus Leave unassigned. */
export const ProviderPicker: FC<{
  providers: ProviderOption[];
  value: string;
  onChange: (id: string) => void;
  busy: Record<string, string>;
}> = ({ providers, value, onChange, busy }) => (
  <Box role="radiogroup" aria-label="Provider" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    {[...providers, { id: '', name: 'Leave unassigned' }].map((p) => {
      const on = p.id === value;
      const note = !p.id ? 'Front desk assigns later' : busy[p.id] ?? busy['*'] ?? 'Free';
      const free = note === 'Free';
      return (
        <ButtonBase
          key={p.id || 'unassigned'}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(p.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1,
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: on ? 'primary.main' : '#E5E7EB',
            bgcolor: on ? '#FFFBF6' : 'background.paper',
          }}
        >
          <Box sx={{ flex: 'none', width: 18, height: 18, boxSizing: 'border-box', borderRadius: '50%', border: on ? '5px solid' : '1.5px solid', borderColor: on ? 'primary.main' : '#9CA3AF' }} />
          <Box sx={{ flex: 'none', width: 30, height: 30, borderRadius: '50%', bgcolor: '#EFE7DC', color: '#3B2E1A', fontSize: '0.68rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {p.id ? providerInitials(p.name) : '?'}
          </Box>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.88rem', fontWeight: 600 }} noWrap>{p.name}</Typography>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, fontSize: '0.78rem', fontWeight: 500, color: !p.id ? '#4B5563' : free ? '#1D5C38' : '#7A4314', textAlign: 'right' }}>
            <Box component="span" sx={{ flex: 'none', width: 7, height: 7, borderRadius: '50%', bgcolor: !p.id ? '#9CA3AF' : free ? '#2F8F57' : '#C9772B' }} />
            {note}
          </Box>
        </ButtonBase>
      );
    })}
  </Box>
);

/** Appointment types as colour chips (the clinic's catalog colours). */
/** Add an appointment type: the Service Pricing window locked to appointments, plus its colour. */
export const AddTypeDialog: FC<{
  open: boolean;
  onClose: () => void;
  onSaved?: (item: ServiceCatalogItem) => void;
}> = ({ open, onClose, onSaved }) => (
  <PricingItemDialog open={open} onClose={onClose} defaultCategory="appointment" lockCategory withColor onSaved={onSaved} />
);

const ADD_TYPE = '__add_appointment_type__';
const dot = (color?: string) => (
  <Box component="span" sx={{ flex: 'none', width: 9, height: 9, borderRadius: '50%', bgcolor: color ?? 'transparent', border: color ? 0 : '1px solid #D1D5DB' }} />
);

/**
 * The type dropdown in New appointment and Edit: the clinic's types with their colours, free typing
 * for a one-off type, and "Add appointment type" last for whoever may add types (online only).
 */
export const TypeSelect: FC<{
  types: Array<{ name: string; color: string }>;
  value: string;
  onChange: (name: string) => void;
}> = ({ types, value, onChange }) => {
  const rbac = useRBAC();
  const typeCan = calendarSettingsControls(rbac);
  const isOffline = useIsOffline();
  const canAdd = typeCan.manageTypes && !isOffline;
  const [adding, setAdding] = useState(false);
  // Picking "Add appointment type" makes MUI clear the box; a cancelled window puts it back.
  const beforeAddRef = useRef('');
  const colorOf = (name: string) => types.find((t) => t.name.toLowerCase() === name.toLowerCase())?.color;

  return (
    <>
      <Autocomplete
        freeSolo
        options={types.map((t) => t.name)}
        value={value}
        onChange={(_e, next) => {
          if (next === ADD_TYPE) {
            beforeAddRef.current = value;
            setAdding(true);
            return;
          }
          onChange(next || '');
        }}
        onInputChange={(_e, next) => {
          if (next !== ADD_TYPE) onChange(next);
        }}
        getOptionLabel={(option) => (option === ADD_TYPE ? '' : option)}
        filterOptions={(options, params) => {
          const shown = options.filter((o) => o.toLowerCase().includes(params.inputValue.toLowerCase()));
          return canAdd ? [...shown, ADD_TYPE] : shown;
        }}
        renderOption={(props, option) =>
          option === ADD_TYPE ? (
            <li {...props} key={option}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pt: 1, borderTop: '1px solid', borderColor: 'divider', color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>
                <AddIcon fontSize="small" /> Add appointment type
              </Box>
            </li>
          ) : (
            <li {...props} key={option}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {dot(colorOf(option))}
                {option}
              </Box>
            </li>
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Choose or type a visit type"
            inputProps={{ ...params.inputProps, 'aria-label': 'Type' }}
            InputProps={{ ...params.InputProps, startAdornment: value ? <Box sx={{ display: 'inline-flex', ml: 0.5 }}>{dot(colorOf(value))}</Box> : undefined }}
          />
        )}
      />
      <AddTypeDialog
        open={adding}
        onClose={() => {
          setAdding(false);
          onChange(value || beforeAddRef.current);
        }}
        onSaved={(item) => {
          beforeAddRef.current = item.name;
          onChange(item.name);
        }}
      />
    </>
  );
};

/** Status chips (canvas: a radio group of pills, a dashed edge for Tentative). */
export const StatusChips: FC<{
  value: AppointmentStatus;
  onChange: (status: AppointmentStatus) => void;
  options?: AppointmentStatus[];
  disabled?: boolean;
}> = ({ value, onChange, options, disabled }) => (
  <Box role="radiogroup" aria-label="Status" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {STATUSES.filter((s) => (options ? options.includes(s.value) : s.value !== 'cancelled')).map((s) => {
      const on = s.value === value;
      return (
        <ButtonBase
          key={s.value}
          role="radio"
          aria-checked={on}
          disabled={disabled}
          onClick={() => onChange(s.value)}
          sx={{
            height: 34,
            px: 1.5,
            gap: 0.75,
            borderRadius: 999,
            fontSize: '0.82rem',
            fontWeight: on ? 600 : 500,
            color: on ? s.ink : '#374151',
            bgcolor: on ? s.fill : 'background.paper',
            border: '1px',
            borderStyle: on && s.value === 'tentative' ? 'dashed' : 'solid',
            borderColor: on ? s.dot : '#E5E7EB',
          }}
        >
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.dot }} />
          {s.label}
        </ButtonBase>
      );
    })}
  </Box>
);
